import { useState } from "react";
import { Terminal, CaretDown, CaretUp } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface RunCommandCardProps {
  command: string;
  output: string;
  exitCode: number;
  compact?: boolean;
}

export function RunCommandCard({
  command,
  output,
  exitCode,
  compact,
}: RunCommandCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isSuccess = exitCode === 0;
  const lineCount = output.split("\n").length;
  const shouldCollapse = lineCount > 8;

  return (
    <div
      className={cn(
        "my-1 w-full max-w-[34rem] rounded bg-foreground/[0.03] overflow-hidden",
        compact && "text-[12px]",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground min-w-0">
          <Terminal size={12} weight="Bold" />
          <span className="uppercase tracking-wide">/run</span>
          <code className="font-mono text-foreground truncate max-w-[30ch]">
            {command}
          </code>
        </div>
        <span
          className={cn(
            "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0",
            isSuccess
              ? "bg-success/15 text-success"
              : "bg-destructive/15 text-destructive",
          )}
        >
          exit {exitCode}
        </span>
      </div>

      {/* Output */}
      {output && (
        <div
          className="px-3 py-2 overflow-x-auto"
          style={{
            maxHeight: shouldCollapse && !expanded ? "160px" : "400px",
          }}
        >
          <pre className="font-mono text-[11px] leading-[1.4] text-foreground whitespace-pre-wrap break-all">
            {output}
          </pre>
        </div>
      )}

      {/* Collapse toggle */}
      {shouldCollapse && (
        <Button
          type="button"
          variant="ghost"
          onClick={() => setExpanded((v) => !v)}
          className="h-auto w-full justify-center gap-1 rounded-none border-t border-border/50 px-3 py-1 text-[11px] text-muted-foreground"
        >
          {expanded ? (
            <>
              <CaretUp size={10} /> Show less
            </>
          ) : (
            <>
              <CaretDown size={10} /> Show all {lineCount} lines
            </>
          )}
        </Button>
      )}
    </div>
  );
}
